import { useState } from 'react';
import { deleteTemplateOverride, fetchTemplates, upsertTemplate } from '@/api/templates';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TextField } from '@/components/ui/TextField';
import { TextArea } from '@/components/ui/TextArea';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { TEMPLATE_PLACEHOLDERS } from '@/constants/defaults';
import { humanizeEnum } from '@/lib/format';
import { errorMessageOf } from '@/lib/errors';
import type { NotificationTemplate } from '@/types';

export function TemplatesSection() {
  const { data, errorMessage, isLoading, reload } = useAsyncData<NotificationTemplate[] | null>(
    (signal) => fetchTemplates(signal),
    [],
  );

  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  function openEdit(template: NotificationTemplate) {
    setEditing(template);
    setBody(template.body ?? '');
    setSubject(template.subject ?? '');
    setIsActive(template.isActive);
  }

  async function handleSave() {
    if (!editing) {
      return;
    }
    setIsBusy(true);
    try {
      await upsertTemplate({
        templateKey: editing.templateKey,
        channel: editing.channel,
        body,
        subject: editing.channel === 'EMAIL' ? subject : editing.subject ?? null,
        language: editing.language ?? 'en',
        isActive,
      });
      toast.success('Template saved');
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleRevert() {
    if (!editing) {
      return;
    }
    setIsBusy(true);
    try {
      await deleteTemplateOverride(editing.templateKey, editing.channel);
      toast.success('Reverted to default');
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  if (isLoading) {
    return <LoadingState message="Loading templates…" />;
  }
  if (errorMessage) {
    return <ErrorState message={errorMessage} onRetry={reload} />;
  }

  const templates = data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h2 className="text-base font-semibold text-ink">Notification templates</h2>
        <p className="mt-0.5 text-sm text-ink-muted">
          Customise the messages customers receive at each stage. Blank overrides fall back to the shared default.
        </p>
        <ul className="mt-4 divide-y divide-line">
          {templates.map((template) => (
            <li
              key={`${template.templateKey}-${template.channel}`}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink">{humanizeEnum(template.templateKey)}</span>
                  <Badge tone="neutral">{template.channel}</Badge>
                  {template.isCustom ? <Badge tone="success">Custom</Badge> : <Badge tone="neutral">Default</Badge>}
                </div>
                <p className="mt-0.5 max-w-xl truncate text-xs text-ink-subtle">{template.body}</p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => openEdit(template)}>
                Edit
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `${humanizeEnum(editing.templateKey)} · ${editing.channel}` : ''}
        size="lg"
        footer={
          <>
            {editing?.isCustom ? (
              <Button variant="ghost" onClick={handleRevert} disabled={isBusy}>
                Revert to default
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isBusy} disabled={!body.trim()}>
              Save template
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="flex flex-col gap-3">
            {editing.channel === 'EMAIL' ? (
              <TextField label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} />
            ) : null}
            <TextArea
              label={editing.channel === 'WHATSAPP' ? 'Approved template name' : 'Message body'}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={5}
            />
            <Toggle label="Active" checked={isActive} onChange={setIsActive} />
            <div>
              <p className="mb-1 text-xs font-medium text-ink-muted">Available placeholders</p>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATE_PLACEHOLDERS.map((placeholder) => (
                  <code key={placeholder} className="rounded bg-surface-sunken px-1.5 py-0.5 text-[11px] text-ink-muted">
                    {placeholder}
                  </code>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
